import { ArrowRight, Wallet } from "lucide-react";
import type { MensalistaDaQuinzena, AdicionalAgregado } from "@/hooks/use-equipe";
import { MESES_LABEL } from "@/components/equipe/nota-mensal";
import {
  formatarBRL, formatarDataCurta, paraReais, paraCentavos,
  separarMensalistasPorAdiantamento, netLancamentosCentavos,
  adiantamentoSaiNestaQuinzena,
} from "@shared/equipe-fechamento";

/**
 * Financeiro v3 (D-M2) — na aba Quinzena o mensalista NÃO tem linha. O que
 * sobra dele é o DINHEIRO que sai junto na data de pagamento: o adiantamento
 * (gerado, ou ainda "a gerar") e os adicionais dele (que saem na quinzena —
 * D-M4). Os salários do mês são só um lembrete com link para a aba Mês.
 *
 * Renderiza SÓ quando há algo — sem mensalista com dinheiro nesta quinzena, a
 * faixa não existe.
 */
export function SaiJuntoNestaData({
  mensalistas, agregadoMensalistas, competencia, salariosCentavos, salariosPrevisto, dataFechamento, onIrParaMes,
}: {
  mensalistas: MensalistaDaQuinzena[];
  /** Agregado de `tarefa_adicional` da quinzena, JÁ filtrado para MENSALISTA e para o que sai no caixa. */
  agregadoMensalistas: AdicionalAgregado[];
  /** `competenciaDaQuinzena(quinzena)` — o mês em que o adiantamento abate. */
  competencia: string;
  salariosCentavos: number;
  salariosPrevisto: boolean;
  dataFechamento: string;
  onIrParaMes: () => void;
}) {
  // `comAdiantamento` = quem já tem o da COMPETÊNCIA resolvido; `semAdiantamento`
  // = quem ainda precisa gerar. Nenhum dos dois decide dinheiro — quem sai NESTA
  // data é só quem tem `adiantamento_id` (o desta quinzena).
  const { comAdiantamento, semAdiantamento } = separarMensalistasPorAdiantamento(mensalistas);
  const nomeDe = (m: { nome: string; apelido: string | null }) => m.apelido ?? m.nome;
  const mesLabel = MESES_LABEL[Number(competencia.slice(5, 7)) - 1] ?? competencia;

  // Adicionais por pessoa (net do que sai no caixa — o desconto de mensalista já
  // foi filtrado antes de chegar aqui).
  const adicionalPorPessoa = new Map<string, number>();
  for (const a of agregadoMensalistas) {
    adicionalPorPessoa.set(a.funcionario_id, (adicionalPorPessoa.get(a.funcionario_id) ?? 0) + netLancamentosCentavos([{ sinal: a.sinal, valor: a.total }]));
  }

  const itens: React.ReactNode[] = [];
  // SAI NESTA DATA: só o adiantamento pendurado NESTA quinzena.
  for (const m of comAdiantamento.filter(adiantamentoSaiNestaQuinzena)) {
    itens.push(
      <span key={`adi-${m.id}`}>
        adiantamento de <strong>{nomeDe(m)}</strong> <strong>{formatarBRL(m.adiantamento_valor)}</strong>
        <span className="text-muted-foreground"> ({mesLabel}{m.adiantamento_status === "PAGO" ? " · pago" : ""})</span>
      </span>,
    );
  }
  // JÁ SAIU, em outra quinzena do MESMO mês (competência com duas quinzenas —
  // o caso de agosto/2026). Informação, fora de qualquer soma: sem isto a
  // pessoa some da faixa e volta a parecer que falta gerar.
  const jaPagoEmOutraQuinzena = comAdiantamento.filter(
    m => !adiantamentoSaiNestaQuinzena(m) && m.adiantamento_comp_id != null,
  );
  for (const { mensalista: m, valorSugerido } of semAdiantamento) {
    itens.push(
      <span key={`ger-${m.id}`}>
        adiantamento de <strong>{nomeDe(m)}</strong> <strong>{formatarBRL(valorSugerido)}</strong>
        <span className="text-muted-foreground"> ({mesLabel} · </span><span className="text-erro font-bold">a gerar</span><span className="text-muted-foreground">)</span>
      </span>,
    );
  }
  const nomePorId = new Map(mensalistas.map(m => [m.id, nomeDe(m)]));
  Array.from(adicionalPorPessoa.entries()).forEach(([id, centavos]) => {
    if (centavos === 0) return;
    itens.push(
      <span key={`adc-${id}`}>
        {centavos > 0 ? "adicional" : "desconto"} de <strong>{nomePorId.get(id) ?? "mensalista"}</strong>{" "}
        <strong className={centavos > 0 ? "text-sucesso" : "text-erro"}>{centavos > 0 ? "+" : "−"} {formatarBRL(paraReais(Math.abs(centavos)))}</strong>
      </span>,
    );
  });

  if (itens.length === 0 && jaPagoEmOutraQuinzena.length === 0 && paraCentavos(paraReais(salariosCentavos)) === 0) return null;

  return (
    <button
      type="button"
      onClick={onIrParaMes}
      className="w-full text-left flex items-start gap-2 rounded-xl border border-info/30 bg-info-suave px-3 py-2 text-xs text-foreground hover:bg-info/10 transition-colors"
      data-testid="faixa-sai-junto"
    >
      <Wallet className="h-3.5 w-3.5 text-info shrink-0 mt-0.5" />
      <span className="min-w-0 flex-1 leading-relaxed">
        {itens.length > 0 && (
          <>
            Nesta data de pagamento sai também:{" "}
            {itens.map((it, i) => <span key={i}>{i > 0 && <span className="text-muted-foreground"> · </span>}{it}</span>)}
            {". "}
          </>
        )}
        Salários de {mesLabel}: <strong>{formatarBRL(paraReais(salariosCentavos))}</strong>
        {salariosPrevisto && <span className="text-muted-foreground"> (previsto)</span>}
        {" "}em <strong>{formatarDataCurta(dataFechamento)}</strong>
        {jaPagoEmOutraQuinzena.length > 0 && (
          <span className="block text-muted-foreground mt-0.5">
            {jaPagoEmOutraQuinzena.map((m, i) => (
              <span key={m.id}>
                {i > 0 && " · "}
                adiantamento de {mesLabel} de <strong>{nomeDe(m)}</strong>: {formatarBRL(m.adiantamento_comp_valor)}
                {" "}{m.adiantamento_comp_status === "PAGO" ? "já pago" : "previsto"} com a quinzena de{" "}
                {formatarDataCurta(m.adiantamento_comp_quinzena_data_pagamento ?? m.adiantamento_comp_quinzena_fim)}
              </span>
            ))}
            {" — não sai nesta data"}
          </span>
        )}
      </span>
      <span className="shrink-0 inline-flex items-center gap-1 font-semibold text-info whitespace-nowrap">
        Mensalistas · mês <ArrowRight className="h-3.5 w-3.5" />
      </span>
    </button>
  );
}
